import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, CITIES, CUISINES } from "@/lib/api";
import { MapView } from "@/components/MapView";
import { TruckCard } from "@/components/TruckCard";
import { ConciergeWidget } from "@/components/ConciergeWidget";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, SlidersHorizontal } from "lucide-react";

export default function Explore() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [trucks, setTrucks] = useState([]);
  const [favs, setFavs] = useState([]);
  const [q, setQ] = useState("");
  const [city, setCity] = useState(params.get("city") || "All");
  const [cuisine, setCuisine] = useState("All");
  const [liveOnly, setLiveOnly] = useState(false);
  const [activeCenter, setActiveCenter] = useState(null);

  const load = useCallback(() => {
    const p = {};
    if (city !== "All") p.city = city;
    if (cuisine !== "All") p.cuisine = cuisine;
    if (liveOnly) p.live = true;
    if (q) p.q = q;
    api.get("/trucks", { params: p }).then((r) => setTrucks(r.data)).catch(() => {});
  }, [city, cuisine, liveOnly, q]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user) api.get("/favorites").then((r) => setFavs(r.data.truck_ids)).catch(() => {});
  }, [user]);

  const toggleFav = async (truck) => {
    if (!user) { toast.error("Log in to save favorites"); return; }
    try {
      const { data } = await api.post(`/favorites/${truck.id}`);
      setFavs((f) => data.favorited ? [...f, truck.id] : f.filter((x) => x !== truck.id));
      toast.success(data.favorited ? `Saved ${truck.name}` : `Removed ${truck.name}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col lg:flex-row">
      {/* LIST */}
      <div className="lg:w-[42%] xl:w-[38%] flex flex-col border-r border-teal-100 bg-[#FAF9F6]">
        <div className="p-5 border-b border-teal-100 gr-glass sticky top-0 z-10">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search trucks or cuisine" className="pl-9 rounded-full border-teal-200 bg-white" data-testid="explore-search" />
          </div>
          <div className="flex gap-2 mt-3 flex-wrap items-center">
            <SlidersHorizontal className="w-4 h-4 text-slate-400" />
            <Chip active={city === "All"} onClick={() => setCity("All")}>All cities</Chip>
            {CITIES.map((c) => <Chip key={c} active={city === c} onClick={() => setCity(c)} testid={`filter-city-${c}`}>{c}</Chip>)}
            <Chip active={liveOnly} onClick={() => setLiveOnly((v) => !v)} testid="filter-live">● Live now</Chip>
          </div>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Chip active={cuisine === "All"} onClick={() => setCuisine("All")}>All food</Chip>
            {CUISINES.map((c) => <Chip key={c} active={cuisine === c} onClick={() => setCuisine(c)}>{c}</Chip>)}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <p className="text-sm text-slate-500 font-semibold" data-testid="explore-count">{trucks.length} trucks found</p>
          {trucks.map((t, i) => (
            <TruckCard key={t.id} truck={t} index={i} onHover={(tr) => tr.lat && setActiveCenter([tr.lat, tr.lng])} onFavorite={toggleFav} favorited={favs.includes(t.id)} />
          ))}
          {trucks.length === 0 && <p className="text-slate-400 text-center py-12">No trucks match your filters yet.</p>}
        </div>
      </div>

      {/* MAP */}
      <div className="lg:w-[58%] xl:w-[62%] h-[50vh] lg:h-auto relative">
        <MapView trucks={trucks} activeCenter={activeCenter} />
      </div>
      <ConciergeWidget />
    </div>
  );
}

const Chip = ({ children, active, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${active ? "bg-[#0FB5A6] text-white" : "bg-white text-slate-600 border border-teal-200 hover:bg-teal-50"}`}
  >
    {children}
  </button>
);
